import RadioButton from "@medoma/components/RadioButton/RadioButton";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import { useFormContext } from "react-hook-form";
import type { ITimeSlotsFields } from "./TimeSlots";
import { TimeSlots } from "./TimeSlots";
import { useEffect } from "react";
import type { IRecurrencesPerDayOption } from "./timeSlotsUtils";
import { recurrencesPerDayOptions } from "./timeSlotsUtils";
import {
  timeOfDayDictionary,
  timeOfDaySchema,
  type ITimeOfDay,
} from "@medoma/models/activities";
import { useLingui } from "@lingui/react/macro";
import {
  TimeSensitivityPicker,
  type ITimeSensitivityFields,
} from "./TimeSensitivityPicker";

export type ITimeSlotsPickerFields = ITimeSlotsFields &
  ITimeSensitivityFields & {
    timeCategory: ITimeOfDay;
    recurrencesPerDay: IRecurrencesPerDayOption;
  };

export const TimeSlotsPicker = ({ isRecurring }: { isRecurring: boolean }) => {
  const { t } = useLingui();
  const {
    formState: { errors },
    getValues,
    register,
    resetField,
    setValue,
    watch,
  } = useFormContext<ITimeSlotsPickerFields>();

  const timeCategory = watch("timeCategory");
  const isAnyTimeOfDay = timeCategory === timeOfDaySchema.enum.Any;
  const isSpecificTimeOfDay = timeCategory === timeOfDaySchema.enum.Specific;
  const recurrencesPerDay = Number(watch("recurrencesPerDay"));

  // Default recurrencesPerDay to "1" only when no value is set yet. Preserves
  // values from the form's defaultValues (e.g. when creating an activity from
  // a template that already specifies a number of occurrences per day).
  useEffect(() => {
    if (isSpecificTimeOfDay && !getValues("recurrencesPerDay")) {
      setValue("recurrencesPerDay", "1");
    }
  }, [isSpecificTimeOfDay, setValue, getValues]);

  // Reset fields when changing time category
  useEffect(() => {
    if (!isRecurring) {
      resetField(`recurrencesPerDay`);
      resetField(`timeslots`);
    }
  }, [isRecurring, resetField]);

  const timeCategoryField = register(`timeCategory`, {
    required: {
      value: true,
      message: t`Välj tid på dagen`,
    },
    onChange: () => {
      resetField(`timeSensitivity`);
      resetField(`recurrencesPerDay`);
      resetField(`timeslots`);
    },
  });

  return (
    <>
      <RadioButtons
        legend={t`Tid på dagen`}
        errorMessage={errors.timeCategory?.message}
        orientation="horizontal"
      >
        <RadioButton
          label={{ text: t(timeOfDayDictionary.Specific.short) }}
          visualStyle="framed"
          value={timeOfDaySchema.enum.Specific}
          {...timeCategoryField}
        />
        <RadioButton
          label={{ text: t(timeOfDayDictionary.Any.short) }}
          visualStyle="framed"
          value={timeOfDaySchema.enum.Any}
          {...timeCategoryField}
        />
      </RadioButtons>
      {isRecurring && !isAnyTimeOfDay ? (
        <>
          <RadioButtons
            legend={t`Antal tillfällen per dygn`}
            orientation="horizontal"
          >
            {recurrencesPerDayOptions.map((recurrencesPerDay) => (
              <RadioButton
                key={recurrencesPerDay}
                label={{ text: recurrencesPerDay }}
                {...register(`recurrencesPerDay`, {
                  required: {
                    value: true,
                    message: t`Välj antal tillfällen per dygn`,
                  },
                })}
                value={recurrencesPerDay}
                visualStyle="framed"
              />
            ))}
          </RadioButtons>
        </>
      ) : (
        <></>
      )}
      {!isAnyTimeOfDay && isSpecificTimeOfDay ? (
        <>
          <TimeSlots recurrences={recurrencesPerDay} />
          <TimeSensitivityPicker />
        </>
      ) : (
        <></>
      )}
    </>
  );
};
